import { useEffect, useState } from "react";
import { Home, Cloud, Droplets, Sun } from "lucide-react";
import { api } from "@/lib/api";

const MONTHS = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];

/**
 * WeatherSparkline + HousingCard — small "how you'll live" panels
 * that read from `/universities/{id}/lifestyle-signals`.
 *
 * Composition:
 *   • Sparkline: bar strip of monthly rainfall + coloured temp dots
 *   • Housing card: which years you're guaranteed uni accommodation +
 *     rough monthly rent
 *
 * Both hide gracefully if the endpoint returns null (e.g. uni in a
 * country we don't have curated climate for yet).
 */
export default function LifestyleExtras({ uni, format }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/universities/${uni.id}/lifestyle-signals`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [uni.id]);

  if (!data) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="life-lifestyle-extras">
      {data.climate && <WeatherSparkline climate={data.climate} />}
      {data.housing && <HousingCard housing={data.housing} format={format} />}
    </div>
  );
}

function WeatherSparkline({ climate }) {
  const ts = climate.t || [];
  const ps = climate.p || [];
  if (!ts.length) return null;
  const maxP = Math.max(...ps) || 1;
  const maxT = Math.max(...ts);
  const minT = Math.min(...ts);
  const tColour = (t) => {
    if (t <= 4) return "#3b82f6";
    if (t <= 10) return "#0891b2";
    if (t <= 16) return "#059669";
    if (t <= 22) return "#B45309";
    return "#9F1239";
  };
  return (
    <div className="border border-[#002147]/10 bg-white/40 p-5" data-testid="life-weather">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#002147]/60 flex items-center gap-1.5">
          <Cloud className="h-3 w-3" /> 12-month climate
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#002147]/40">
          {climate.city || "—"}
        </div>
      </div>
      <div className="flex items-end gap-1 h-24">
        {ps.map((p, i) => {
          const h = (p / maxP) * 100;
          return (
            <div key={i} className="flex flex-col items-center justify-end flex-1 min-w-0">
              <div
                className="w-full bg-[#7C3AED]/25 hover:bg-[#7C3AED]/50 transition-colors"
                style={{ height: `${Math.max(6, h)}%` }}
                title={`${MONTHS[i]} · ${p}mm rain · ${ts[i]}°C avg`}
              />
            </div>
          );
        })}
      </div>
      <div className="flex gap-1 mt-1">
        {ts.map((t, i) => (
          <div key={i} className="flex flex-col items-center flex-1 min-w-0">
            <div className="h-2 w-2 rounded-full mt-0.5" style={{ backgroundColor: tColour(t) }} />
            <div className="font-mono text-[8px] text-[#002147]/50 mt-0.5">{MONTHS[i][0]}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 pt-3 border-t border-[#002147]/10 grid grid-cols-3 gap-2 text-center">
        <div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-[#002147]/50 flex items-center justify-center gap-1">
            <Sun className="h-3 w-3" /> Summer
          </div>
          <div className="font-serif text-lg text-[#002147] leading-none mt-1">{maxT}°C</div>
        </div>
        <div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-[#002147]/50">
            Winter
          </div>
          <div className="font-serif text-lg text-[#002147] leading-none mt-1">{minT}°C</div>
        </div>
        <div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-[#002147]/50 flex items-center justify-center gap-1">
            <Droplets className="h-3 w-3" /> Yr rain
          </div>
          <div className="font-serif text-lg text-[#002147] leading-none mt-1">
            {ps.reduce((a, b) => a + b, 0)}mm
          </div>
        </div>
      </div>
    </div>
  );
}

function HousingCard({ housing, format }) {
  const yrs = housing.guaranteed_years || [];
  const total = housing.typical_course_years || 3;
  const isFullGuarantee = yrs.length >= total;
  const tone = isFullGuarantee ? "#065F46" : yrs.length >= 2 ? "#0F766E" : "#B45309";
  return (
    <div className="border border-[#002147]/10 bg-white/40 p-5" data-testid="life-housing">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#002147]/60 flex items-center gap-1.5">
          <Home className="h-3 w-3" /> Housing guarantee
        </div>
        <div
          className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border rounded-none"
          style={{ borderColor: tone, color: tone }}
        >
          {isFullGuarantee ? "Full course" : `${yrs.length} year${yrs.length === 1 ? "" : "s"}`}
        </div>
      </div>
      {/* Years bar */}
      <div className="flex gap-1 mb-3">
        {Array.from({ length: total }, (_, i) => {
          const yr = i + 1;
          const g = yrs.includes(yr);
          return (
            <div
              key={yr}
              className="flex-1 h-8 flex items-center justify-center border font-mono text-xs uppercase tracking-widest"
              style={{
                backgroundColor: g ? tone : "transparent",
                borderColor: g ? tone : "#00214733",
                color: g ? "#F3F1EC" : "#00214788",
              }}
            >
              Yr {yr}
            </div>
          );
        })}
      </div>
      <p className="font-sans text-xs text-[#002147]/70 leading-relaxed">{housing.note}</p>
      {housing.monthly_rent_usd != null && (
        <div className="mt-3 pt-3 border-t border-[#002147]/10 flex justify-between items-baseline">
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#002147]/50">
            Typical monthly rent
          </span>
          <span className="font-serif text-lg text-[#002147]">
            {typeof format === "function" ? format(housing.monthly_rent_usd) : `$${housing.monthly_rent_usd}`}
          </span>
        </div>
      )}
    </div>
  );
}
